/* eslint-disable no-console */
import { useRecoilValue, useRecoilState, useResetRecoilState } from 'recoil';
import { parseAccessToken } from '../helpers/Util';
import gatewayAtom from '../../recoil/config/gatewayAtom';
import accessTokenAtom from '../../recoil/user/accessTokenAtom';

const clientSecret = 'secret'; // Not always the case???

/**
 * Reads whatever `httpRequest.send` rejected with.
 *
 * The body is read through `clone()`, so this can be called any number of times and from more
 * than one level — the original Response is never consumed and stays readable for the next
 * caller.
 *
 * Never throws. A network failure (fetch rejects with a TypeError), an HTML error page from the
 * gateway, or a plain Error all come back as an empty body and an undefined status, which the
 * error mappers read as DEFAULT.
 *
 * @param {(Response|Error|unknown)} err
 * @returns {Promise<{status: (number|undefined), body: object}>}
 */
export const parseErrorResponse = async (err) => {
  const status = err?.status;

  if (typeof err?.clone !== 'function') {
    return { status, body: {} };
  }

  try {
    const body = await err.clone().json();

    return { status, body: body || {} };
  } catch (parseError) {
    // not our JSON — the status alone has to carry the message
    return { status, body: {} };
  }
};

// shared auth promise that can be used to refresh the token only once
let authPromise = null;

const useHttpRequest = () => {
  const gatewayUrl = useRecoilValue(gatewayAtom);
  const [accessToken, setAccessToken] = useRecoilState(accessTokenAtom);
  const resetAccessToken = useResetRecoilState(accessTokenAtom);

  const buildRequestOptions = (token, context) => {
    const {
      method = 'GET',
      body,
      additionalHeaders,
    } = context || {};

    const requestOptions = {
      method,
      headers: {
        authorization: `Bearer ${token}`,
        ...additionalHeaders,
      },
      mode: 'cors',
      credentials: 'include',
    };

    if (body) {
      requestOptions.body = JSON.stringify(body);
    }

    return requestOptions;
  };

  const reauthenticate = async (token) => {
    try {
      const { sessionId, network, userAgent } = parseAccessToken(token);

      const formData = new URLSearchParams();
      formData.append('grant_type', 'urn:ietf:params:oauth:grant-type:token-exchange');
      formData.append('subject_token', sessionId);
      formData.append('subject_token_type', 'urn:ietf:params:oauth:token-type:session');
      // eslint-disable-next-line no-buffer-constructor
      const basicAuth = Buffer.from(`${network}:${clientSecret}`).toString('base64');
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Basic ${basicAuth}`,
      };

      if (userAgent) {
        headers['User-Agent'] = userAgent;
        headers['X-User-Agent'] = userAgent;
      }

      const requestOptions = {
        headers,
        credentials: 'include',
        method: 'POST',
        body: formData.toString(),
      };

      const authUrl = gatewayUrl.replace('api', 'auth');

      const resultAuth = await fetch(`${authUrl}/oauth/token`, requestOptions);

      if (!resultAuth.ok) {
        resetAccessToken();

        console.log('Unable to reauthenticate the user', resultAuth);

        throw resultAuth;
      }

      const { access_token: newAccessToken } = await resultAuth.json();

      setAccessToken(newAccessToken);

      return newAccessToken;
    } finally {
      // reset the promise for the next time
      authPromise = null;
    }
  };

  const sendRequest = async (path, context, token) => {
    console.debug(`fetching: ${gatewayUrl}${path}`);

    let tokenToUse = token;

    // if we are in the middle of fetching the auth token
    // wait for it to complete, and use that token instead
    if (authPromise) {
      tokenToUse = await authPromise;
    }

    let result = await fetch(`${gatewayUrl}${path}`, buildRequestOptions(tokenToUse, context));

    if (!result.ok) {
      if (result.status === 401) {
        // only reauthenticate once,
        // making all concurrent request calls wait for the token
        authPromise = authPromise || reauthenticate(token);

        try {
          // wait for the refresh token to come back
          tokenToUse = await authPromise;
          result = await fetch(`${gatewayUrl}${path}`, buildRequestOptions(tokenToUse, context));

          if (!result.ok) {
            throw result;
          }
        } catch (reauthenticateError) {
          // throw original error from the request, and not the auth error
          throw result;
        }
      } else {
        throw result;
      }
    }

    return result.json();
  };

  return {
    send: (path, context) => sendRequest(path, context, accessToken),
  };
};

export default useHttpRequest;
